import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';

const ManageMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId, memberName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${memberName} ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/members/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Membre supprimé avec succès');
        fetchMembers();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredMembers = members.filter(member =>
    (member.first_name?.toLowerCase() || member.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (member.last_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.card_number.includes(searchTerm) ||
    (member.country?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Actif': return '#2c5f7d';
      case 'Inactif': return '#999';
      case 'Suspendu': return '#c41e3a';
      default: return '#666';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Orlamonde': return '#8b4f6d';
      case 'Magellan': return '#2c5f7d';
      case 'Business': return '#c41e3a';
      default: return '#666';
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: '32px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <h1 className="hero-medium">Gestion des Membres</h1>
          <button
            onClick={() => navigate('/admin/members/add')}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={20} />
            Ajouter un membre
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '24px', position: 'relative' }}>
          <Search size={20} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-secondary)'
          }} />
          <input
            type="text"
            placeholder="Rechercher un membre (nom, email, numéro de carte)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              border: '1px solid var(--border-light)',
              borderRadius: '0px',
              fontSize: '14px',
              fontFamily: 'inherit',
              background: 'var(--bg-primary)'
            }}
          />
        </div>

        {loading ? (
          <p>Chargement...</p>
        ) : filteredMembers.length === 0 ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)'
          }}>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              {searchTerm ? 'Aucun membre ne correspond à votre recherche' : 'Aucun membre enregistré'}
            </p>
          </div>
        ) : (
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            overflowX: 'auto'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Nom</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Email</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Pays</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Carte</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Niveau</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Statut</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'right',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <tr key={member.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td className="body-regular" style={{ padding: '16px' }}>
                      {member.first_name || member.name} {member.last_name || ''}
                    </td>
                    <td className="body-small" style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                      {member.email}
                    </td>
                    <td className="body-small" style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                      {member.country || 'N/A'}
                    </td>
                    <td className="body-small" style={{ padding: '16px', fontFamily: 'monospace' }}>
                      {member.card_number}
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        background: getLevelColor(member.level),
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {member.level}
                      </span>
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        background: getStatusColor(member.status),
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {member.status}
                      </span>
                    </td>
                    <td className="body-small" style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                      {member.expiration}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => navigate(`/admin/members/edit/${member.id}`)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid var(--border-light)',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)'
                          }}
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id, member.name)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid #c41e3a',
                            cursor: 'pointer',
                            color: '#c41e3a'
                          }}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="body-small" style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
          {filteredMembers.length} membre(s) {searchTerm && 'trouvé(s)'}
        </p>
      </div>
    </AdminLayout>
  );
};

export default ManageMembers;